import { Box, Button, HStack, Text, useColorModeValue } from '@chakra-ui/react';
import { useCallback, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import SignaturePad from 'react-signature-canvas';

const MIN_STROKES = 1;
const MIN_POINTS = 10;
const MIN_AREA_PX = 1500; // bounding box width*height in pixels

export interface SignatureCanvasHandle {
  clear: () => void;
  toDataUrl: () => string;
  isValid: () => boolean;
}

interface Props {
  onChange?: (dataUrl: string, valid: boolean) => void;
}

// Normalize a stroke from either signature_pad shape:
//   v1.x: stroke is itself an array of points  -> [Point, Point, ...]
//   v2+:  stroke is { points: Point[], ... }   -> { points: [...] }
function strokePoints(s: any): any[] {
  if (Array.isArray(s)) return s;
  if (s && Array.isArray(s.points)) return s.points;
  return [];
}

function computeBoundingBoxArea(strokes: any[]): number {
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  let any = false;
  for (const s of strokes) {
    for (const p of strokePoints(s)) {
      if (typeof p.x !== 'number' || typeof p.y !== 'number') continue;
      any = true;
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
    }
  }
  if (!any) return 0;
  return Math.max(0, maxX - minX) * Math.max(0, maxY - minY);
}

export const SignatureCanvas = forwardRef<SignatureCanvasHandle, Props>(function SignatureCanvas(
  { onChange },
  ref,
) {
  const padRef = useRef<SignaturePad>(null);
  const [meta, setMeta] = useState({ strokes: 0, points: 0, area: 0 });
  const borderColor = useColorModeValue('gray.300', 'gray.600');

  const validate = useCallback(() => {
    const pad = padRef.current;
    if (!pad) return false;
    const data = pad.toData() as any[];
    const totalPoints = data.reduce(
      (acc: number, s: any) => acc + strokePoints(s).length,
      0,
    );
    const area = computeBoundingBoxArea(data);
    setMeta({ strokes: data.length, points: totalPoints, area });
    return data.length >= MIN_STROKES && totalPoints >= MIN_POINTS && area >= MIN_AREA_PX;
  }, []);

  const handleEnd = useCallback(() => {
    const valid = validate();
    if (onChange && padRef.current) {
      onChange(padRef.current.toDataURL('image/png'), valid);
    }
  }, [onChange, validate]);

  const clear = useCallback(() => {
    padRef.current?.clear();
    setMeta({ strokes: 0, points: 0, area: 0 });
    if (onChange) onChange('', false);
  }, [onChange]);

  useImperativeHandle(
    ref,
    () => ({
      clear,
      toDataUrl: () => padRef.current?.toDataURL('image/png') || '',
      isValid: validate,
    }),
    [clear, validate],
  );

  return (
    <Box border="1px solid" borderColor={borderColor} borderRadius="md" position="relative" bg="white">
      <SignaturePad
        ref={padRef}
        onEnd={handleEnd}
        canvasProps={{ width: 600, height: 200, style: { width: '100%', height: '200px' } }}
      />
      <HStack position="absolute" bottom={2} right={2} spacing={2}>
        <Text fontSize="xs" color="gray.500">
          {meta.strokes} traços, {meta.points} pontos
        </Text>
        <Button size="xs" onClick={clear}>
          Limpar
        </Button>
      </HStack>
    </Box>
  );
});
